/**
 * brandTheme.js — Per-tenant white-label theme engine
 *
 * Each theme is a set of CSS custom property overrides applied to
 * document.documentElement (the :root). Because every component uses
 * var(--clr-*) tokens, changing them here re-skins the entire app
 * instantly with no component rewrites.
 *
 * Usage:
 *   import { applyBrandTheme } from './brandTheme';
 *   applyBrandTheme({ brand_theme: 'midnight', brand_color: '#0f62fe' });
 *   applyBrandTheme(null);  // reset to defaults on logout
 */

// ── Theme presets ─────────────────────────────────────────────────────────────
// Each entry defines CSS var overrides for background layers, borders, and text.
// The accent colour is always driven by brand_color — computed separately.

export const THEMES = {
  default: {
    label:    'Default (Dark Navy)',
    preview:  ['#070C18', '#3B82F6', '#F1F5FF'],
    darkText: true,
    vars:     {},   // use :root stylesheet defaults — no overrides needed
  },
  midnight: {
    label:    'Midnight (IBM-style)',
    preview:  ['#000000', '#0F62FE', '#FFFFFF'],
    darkText: true,
    vars: {
      '--clr-bg-deep':        '#000000',
      '--clr-bg-base':        '#0A0A0A',
      '--clr-bg-surface':     '#111111',
      '--clr-bg-elevated':    '#1C1C1C',
      '--clr-bg-overlay':     '#282828',
      '--clr-border':         '#333333',
      '--clr-border-soft':    '#1E1E1E',
      '--clr-text-primary':   '#FFFFFF',
      '--clr-text-secondary': '#A8A8A8',
      '--clr-text-muted':     '#525252',
      '--clr-text-disabled':  '#3A3A3A',
      '--shadow-sm':          '0 1px 3px rgba(0,0,0,0.7)',
      '--shadow-md':          '0 4px 16px rgba(0,0,0,0.8)',
      '--shadow-lg':          '0 8px 32px rgba(0,0,0,0.9)',
    },
  },
  ocean: {
    label:    'Ocean Blue (Lowes-style)',
    preview:  ['#001845', '#38BDF8', '#E0F2FE'],
    darkText: true,
    vars: {
      '--clr-bg-deep':        '#000F2E',
      '--clr-bg-base':        '#001845',
      '--clr-bg-surface':     '#002266',
      '--clr-bg-elevated':    '#002D80',
      '--clr-bg-overlay':     '#003899',
      '--clr-border':         '#003070',
      '--clr-border-soft':    '#002558',
      '--clr-text-primary':   '#F0F8FF',
      '--clr-text-secondary': '#93C5FD',
      '--clr-text-muted':     '#3B82F6',
      '--clr-text-disabled':  '#1E3A8A',
    },
  },
  forest: {
    label:    'Forest (Dark Green)',
    preview:  ['#071C0E', '#10B981', '#D1FAE5'],
    darkText: true,
    vars: {
      '--clr-bg-deep':        '#020F06',
      '--clr-bg-base':        '#071C0E',
      '--clr-bg-surface':     '#0D2518',
      '--clr-bg-elevated':    '#152E1F',
      '--clr-bg-overlay':     '#1E3D2A',
      '--clr-border':         '#1A3522',
      '--clr-border-soft':    '#112918',
      '--clr-text-primary':   '#F0FFF4',
      '--clr-text-secondary': '#6EE7B7',
      '--clr-text-muted':     '#2D6A4F',
      '--clr-text-disabled':  '#1A4231',
    },
  },
  slate: {
    label:    'Slate (Dark Grey)',
    preview:  ['#0F172A', '#818CF8', '#E2E8F0'],
    darkText: true,
    vars: {
      '--clr-bg-deep':        '#020617',
      '--clr-bg-base':        '#0F172A',
      '--clr-bg-surface':     '#1E293B',
      '--clr-bg-elevated':    '#293548',
      '--clr-bg-overlay':     '#334155',
      '--clr-border':         '#2D3D52',
      '--clr-border-soft':    '#1E2F40',
      '--clr-text-primary':   '#F1F5F9',
      '--clr-text-secondary': '#94A3B8',
      '--clr-text-muted':     '#475569',
      '--clr-text-disabled':  '#2D3F5E',
    },
  },
  light: {
    label:    'Light (Corporate)',
    preview:  ['#F8FAFF', '#2563EB', '#0F172A'],
    darkText: false,
    vars: {
      '--clr-bg-deep':        '#E8EEFF',
      '--clr-bg-base':        '#F8FAFF',
      '--clr-bg-surface':     '#FFFFFF',
      '--clr-bg-elevated':    '#F1F5F9',
      '--clr-bg-overlay':     '#E2E8F0',
      '--clr-border':         '#CBD5E1',
      '--clr-border-soft':    '#E2E8F0',
      '--clr-text-primary':   '#0F172A',
      '--clr-text-secondary': '#334155',
      '--clr-text-muted':     '#64748B',
      '--clr-text-disabled':  '#CBD5E1',
      '--shadow-sm':          '0 1px 3px rgba(0,0,0,0.08)',
      '--shadow-md':          '0 4px 16px rgba(0,0,0,0.10)',
      '--shadow-lg':          '0 8px 32px rgba(0,0,0,0.15)',
    },
  },
};

// All vars this engine can override — needed to reset cleanly on logout
const ALL_THEME_VARS = [
  '--clr-bg-deep', '--clr-bg-base', '--clr-bg-surface', '--clr-bg-elevated',
  '--clr-bg-overlay', '--clr-border', '--clr-border-soft',
  '--clr-text-primary', '--clr-text-secondary', '--clr-text-muted', '--clr-text-disabled',
  '--clr-accent', '--clr-accent-glow', '--clr-accent-light', '--clr-accent-muted',
  '--shadow-sm', '--shadow-md', '--shadow-lg', '--shadow-accent',
];


// ── Colour helpers ────────────────────────────────────────────────────────────

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function toHex(n) {
  return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
}

/** Lighten a hex colour toward white by `amount` (0–1). */
function lighten(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  return `#${toHex(r + (255 - r) * amount)}${toHex(g + (255 - g) * amount)}${toHex(b + (255 - b) * amount)}`;
}

/** Darken a hex colour toward black by `amount` (0–1). */
function darken(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  return `#${toHex(r * (1 - amount))}${toHex(g * (1 - amount))}${toHex(b * (1 - amount))}`;
}

/** Build accent CSS vars from a single hex brand colour + whether the theme is dark. */
function accentVars(color, isDarkTheme) {
  const { r, g, b } = hexToRgb(color);
  return {
    '--clr-accent':       color,
    '--clr-accent-glow':  darken(color, 0.15),
    '--clr-accent-light': isDarkTheme ? lighten(color, 0.30) : darken(color, 0.15),
    '--clr-accent-muted': `rgba(${r},${g},${b},0.15)`,
    '--shadow-accent':    `0 0 20px rgba(${r},${g},${b},0.30)`,
  };
}


// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Apply a tenant's brand to the entire app by overriding CSS custom properties
 * on document.documentElement. Called after login and session restore.
 *
 * Pass null (or call with no args) to reset to the default theme (on logout).
 */
export function applyBrandTheme(brand) {
  const root = document.documentElement;

  // Remove any previously injected overrides so the :root stylesheet takes over
  ALL_THEME_VARS.forEach(v => root.style.removeProperty(v));

  if (!brand) return;

  const themeKey = brand.brand_theme || 'default';
  const theme    = THEMES[themeKey] || THEMES.default;

  // Apply background / text palette overrides
  Object.entries(theme.vars).forEach(([k, v]) => root.style.setProperty(k, v));

  // Apply accent colour scale derived from brand_color
  if (brand.brand_color) {
    const isDark = theme.darkText !== false;   // all themes except 'light' are dark
    Object.entries(accentVars(brand.brand_color, isDark))
      .forEach(([k, v]) => root.style.setProperty(k, v));
  }
}
